/* ═══════════════════════════════════════════════════════
   COREFLOW DESIGN TOKENS
   Single source of truth for ALL UI styling.
   Import: import TK from '../styles/tokens';
   Usage: style={TK.btn} or style={{...TK.input, width: 200}}
   ═══════════════════════════════════════════════════════ */

const BLUE = "#4059AD";
const BORDER = "rgba(217,219,221,0.15)";
const BG_SUBTLE = "rgba(255,255,255,0.06)";
const TEXT = "#FAFAFA";
const TEXT_DIM = "#D9DBDD";
const TEXT_MUTED = "rgba(250,250,250,0.45)";
const RADIUS = 10;

// ── BUTTONS ──────────────────────────────────────────
const btn = {
  padding: "8px 16px",
  borderRadius: RADIUS,
  border: `1px solid ${BORDER}`,
  background: BG_SUBTLE,
  color: TEXT,
  fontSize: 12,
  fontWeight: 400,
  cursor: "pointer",
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  transition: "all .15s ease",
  outline: "none",
};

const btnPrimary = {
  ...btn,
  background: BLUE,
  border: `1px solid ${BLUE}`,
  color: TEXT,
};

const btnSmall = {
  ...btn,
  padding: "5px 12px",
  fontSize: 11,
};

const btnIcon = {
  ...btn,
  padding: "6px 8px",
  width: 32,
  height: 32,
  justifyContent: "center",
};

// Hover/focus styles (apply via onMouseEnter/onMouseLeave)
const btnHoverOn = (e) => {
  e.currentTarget.style.borderColor = BLUE;
  e.currentTarget.style.boxShadow = `0 0 0 2px rgba(64,89,173,0.3)`;
};
const btnHoverOff = (e) => {
  e.currentTarget.style.borderColor = BORDER;
  e.currentTarget.style.boxShadow = "none";
};
const btnActiveOn = (e) => {
  e.currentTarget.style.borderColor = BLUE;
  e.currentTarget.style.boxShadow = `0 0 0 2px rgba(64,89,173,0.3)`;
  e.currentTarget.style.background = BLUE;
};

// ── INPUTS ───────────────────────────────────────────
const input = {
  padding: "8px 12px",
  borderRadius: RADIUS,
  border: `1px solid ${BORDER}`,
  background: BG_SUBTLE,
  color: TEXT,
  fontSize: 12,
  fontWeight: 400,
  fontFamily: "inherit",
  outline: "none",
  transition: "border-color .15s ease, box-shadow .15s ease",
  colorScheme: "dark",
};

const inputFocusOn = (e) => {
  e.currentTarget.style.borderColor = BLUE;
  e.currentTarget.style.boxShadow = `0 0 0 2px rgba(64,89,173,0.3)`;
};
const inputFocusOff = (e) => {
  e.currentTarget.style.borderColor = BORDER;
  e.currentTarget.style.boxShadow = "none";
};

const textarea = {
  ...input,
  resize: "vertical",
  lineHeight: 1.5,
};

const select = {
  ...input,
  cursor: "pointer",
  WebkitAppearance: "none",
  MozAppearance: "none",
  appearance: "none",
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23FAFAFA' stroke-width='1.5' fill='none'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
  paddingRight: 30,
};

// ── SEARCH BAR (matches top global search) ───────────
const searchBar = {
  ...input,
  padding: "8px 12px 8px 36px",
  width: "100%",
  boxSizing: "border-box",
};

// ── CHECKBOX (20px rounded square) ───────────────────
const checkbox = (checked) => ({
  width: 20,
  height: 20,
  borderRadius: 8,
  border: `1.5px solid ${checked ? BLUE : "rgba(217,219,221,0.12)"}`,
  background: checked ? BLUE : "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  flexShrink: 0,
  transition: "all .15s ease",
  padding: 0,
});

// SVG checkmark for checked state
const checkSvg = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none"><polyline points="20,6 9,17 4,12" stroke="#FAFAFA" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/></svg>';

// ── CARDS / CONTAINERS ───────────────────────────────
const card = {
  background: "#2C2E33",
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
};

const cardAlt = {
  background: "#33363D",
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
};

// ── MODAL ────────────────────────────────────────────
const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.5)",
  backdropFilter: "blur(4px)",
  zIndex: 1000,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const modalPanel = {
  background: "#2C2E33",
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
  boxShadow: "0 40px 100px rgba(0,0,0,0.50)",
  maxHeight: "85vh",
  overflowY: "auto",
};

// ── DROPDOWN ─────────────────────────────────────────
const dropdown = {
  background: "#2C2E33",
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS,
  boxShadow: "0 24px 64px rgba(0,0,0,0.80)",
  overflow: "hidden",
};

// ── TABLE ────────────────────────────────────────────
const tableHeader = {
  fontSize: 9,
  fontWeight: 400,
  color: TEXT_MUTED,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  userSelect: "none",
};

const tableRow = {
  borderBottom: "1px solid rgba(217,219,221,0.06)",
  transition: "background .1s ease",
};

const tableRowHoverOn = (e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; };
const tableRowHoverOff = (e) => { e.currentTarget.style.background = "transparent"; };

// ── BADGE ────────────────────────────────────────────
const badge = {
  padding: "3px 8px",
  borderRadius: RADIUS,
  border: `1px solid ${BORDER}`,
  fontSize: 10,
  fontWeight: 400,
  color: TEXT,
  background: "transparent",
};

// ── TEXT ──────────────────────────────────────────────
const heading = {
  fontSize: 18,
  fontWeight: 400,
  color: TEXT,
};

const subheading = {
  fontSize: 13,
  fontWeight: 400,
  color: TEXT_DIM,
};

const label = {
  fontSize: 10,
  fontWeight: 400,
  color: TEXT_MUTED,
  letterSpacing: ".06em",
  textTransform: "uppercase",
};

const body = {
  fontSize: 12,
  fontWeight: 400,
  color: TEXT,
};

// ── CONSTANTS ────────────────────────────────────────
const TK = {
  // Colors
  BLUE, BORDER, BG_SUBTLE, TEXT, TEXT_DIM, TEXT_MUTED, RADIUS,

  // Buttons
  btn, btnPrimary, btnSmall, btnIcon,
  btnHoverOn, btnHoverOff, btnActiveOn,

  // Inputs
  input, inputFocusOn, inputFocusOff,
  textarea, select, searchBar,

  // Checkbox
  checkbox, checkSvg,

  // Layout
  card, cardAlt, dropdown,
  modalBackdrop, modalPanel,

  // Table
  tableHeader, tableRow, tableRowHoverOn, tableRowHoverOff,

  // Typography
  heading, subheading, label, body, badge,
};

export default TK;
